import { memo, type MouseEvent, type ReactNode } from "react";
import { Box, IconButton, Stack, Tooltip, alpha, type Theme } from "@mui/material";
import { History, Print } from "@mui/icons-material";
import { formatDataBR, type EspelhoColaborador, type TipoDia } from "@/api/escala.service";
import {
  formatarHoras,
  proximoTipo,
  type CelulaGrade,
  type DiaColuna,
  type Pendencia,
  type TotaisColaborador,
} from "@/components/escala/espelho";
import { rotuloDoTipo, type EstiloTipo } from "@/components/escala/tiposDia";
import { corDaJornada } from "@/components/escala/cargaHoraria";
import {
  LARGURA_TOTAL,
  cantoAlterado,
  celulaBase,
  contornoSemHorario,
  faixa,
  fixaDireita,
  fixaEsquerda,
  historicoAtenuado,
  larguraColaborador,
  separadorHistorico,
  trianguloAlterado,
} from "@/components/escala/espelhoEstilos";

interface EspelhoLinhaProps {
  colaborador: EspelhoColaborador;
  colunas: DiaColuna[];
  celulas?: Map<number, CelulaGrade>;
  colunasHistorico?: DiaColuna[];
  // data (ISO) do mês anterior -> célula
  celulasHistorico?: Map<string, CelulaGrade>;
  totais: TotaisColaborador;
  // Só com o filtro de domingos ligado: quantos domingos o colaborador trabalha no mês.
  domingosTrabalhados?: number;
  // Escala com carga: as horas do mês na última coluna (TH).
  mostrarHoras?: boolean;
  // Desligado, somem TT, TF e TH; o DT do filtro de domingos fica.
  mostrarTotais?: boolean;
  estilos: Record<string, EstiloTipo>;
  // Fundo contra o qual a cor da hora é ajustada; sem ele, a cor sai como foi escolhida.
  fundoHora?: string;
  onCiclar?: (colaboradorId: string, iso: string, tipo: TipoDia) => void;
  // Botão direito no dia trabalhado (T ou "?") de escala com carga: abre a troca de carga do dia.
  onTrocarCarga?: (colaboradorId: string, iso: string) => void;
  // Botão de log na linha do nome.
  onVerLog?: (colaborador: EspelhoColaborador) => void;
  // Botão da escalinha do colaborador, na linha do nome.
  onImprimirEscalinha?: (colaborador: EspelhoColaborador) => void;
  // data (ISO) -> regras violadas nesse dia
  infracoes?: ReadonlyMap<string, string>;
  pendencia?: Pendencia;
}

// Turma e grupo estratégico vêm no payload mas não apareciam na grade (R51).
function subtitulo(colaborador: EspelhoColaborador) {
  const partes = [colaborador.matricula ? `Mat. ${colaborador.matricula}` : "Sem matrícula"];
  if (colaborador.turma) partes.push(`Turma ${colaborador.turma}`);
  if (colaborador.grupo_estrategico != null) partes.push(`Grupo ${colaborador.grupo_estrategico}`);
  return partes.join(" · ");
}

function iniciais(nome: string) {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? "") + (partes.length > 1 ? partes.at(-1)![0]! : "")).toUpperCase();
}

// Triângulo do dia alterado à mão, no canto superior direito.
export function MarcaAlterado() {
  return <Box component="span" aria-hidden sx={trianguloAlterado()} />;
}

interface SeloTipoProps {
  // O que a célula mostra: o tipo, a hora ou "?".
  tipo: string;
  estilo: EstiloTipo;
  // Contorna a célula que tem infração.
  destaque?: boolean;
  // Cor do texto no lugar da do tipo: a da carga, na hora.
  cor?: string;
  semHorario?: boolean;
  alterado?: boolean;
}

// Célula de um tipo de dia.
export function SeloTipo({ tipo, estilo, destaque, cor, semHorario, alterado }: SeloTipoProps) {
  return (
    <Box
      sx={{
        mx: "auto",
        width: 40,
        height: 28,
        borderRadius: 1.25,
        display: "grid",
        placeItems: "center",
        fontSize: 12,
        bgcolor: estilo.bg,
        color: estilo.fg,
        fontWeight: estilo.peso,
        ...(semHorario && contornoSemHorario(estilo.fg)),
        ...(alterado && cantoAlterado),
        ...(destaque && { outline: "2px solid", outlineColor: "warning.main", outlineOffset: 1 }),
      }}
      style={cor ? { color: cor } : undefined}
    >
      {tipo}
      {alterado && <MarcaAlterado />}
    </Box>
  );
}

// Selo do canto do avatar: vermelho com ocorrência, laranja só com aviso.
export function SeloRevisar({ erro }: { erro: boolean }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        position: "absolute",
        right: -4,
        bottom: -4,
        width: 15,
        height: 15,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 10,
        fontWeight: 800,
        lineHeight: 1,
        color: "common.white",
        bgcolor: erro ? "error.main" : "warning.main",
        border: "2px solid",
        borderColor: "background.paper",
      }}
    >
      !
    </Box>
  );
}

function AvatarDoNome({ titulo, pendencia }: { titulo: string; pendencia?: Pendencia }) {
  const circulo = (
    <Box
      aria-hidden={!pendencia}
      aria-label={pendencia ? `Revisar: ${pendencia.linhas.join("; ")}` : undefined}
      sx={(theme) => ({
        position: "relative",
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 11,
        fontWeight: 700,
        bgcolor: alpha(theme.palette.primary.main, 0.12),
        color: "primary.main",
      })}
    >
      {iniciais(titulo)}
      {pendencia && <SeloRevisar erro={pendencia.erro} />}
    </Box>
  );
  if (!pendencia) return circulo;
  return (
    <Tooltip
      placement="right"
      title={
        <Box>
          <Box sx={{ fontWeight: 700, mb: 0.25 }}>Revisar</Box>
          {pendencia.linhas.map((linha) => (
            <Box key={linha}>{linha}</Box>
          ))}
        </Box>
      }
    >
      {circulo}
    </Tooltip>
  );
}

interface CelulaNomeProps {
  titulo: string;
  subtitulo?: string;
  avatar?: boolean;
  // Botões no fim da linha do nome, como o de log.
  acoes?: ReactNode[];
  // Marca o avatar de quem tem ocorrência ou aviso.
  pendencia?: Pendencia;
}

// Primeira coluna (fixa) da grade: avatar com iniciais, nome e uma linha de detalhe.
export function CelulaNome({ titulo, subtitulo, avatar = true, acoes = [], pendencia }: CelulaNomeProps) {
  // Cada botão ocupa a largura de um botão pequeno; sem descontá-la, o nome longo alarga a coluna.
  const larguraTexto = larguraColaborador((avatar ? 72 : 32) + 38 * acoes.length);
  return (
    <Box component="td" sx={{ ...celulaBase, ...fixaEsquerda, textAlign: "left", pl: 2.5, pr: 1 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        {avatar && <AvatarDoNome titulo={titulo} pendencia={pendencia} />}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            title={titulo}
            sx={{
              fontWeight: 600,
              fontSize: 13,
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: larguraTexto,
            }}
          >
            {titulo}
          </Box>
          {subtitulo && (
            <Box
              title={subtitulo}
              sx={{
                fontSize: 11,
                color: "text.secondary",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: larguraTexto,
              }}
            >
              {subtitulo}
            </Box>
          )}
        </Box>
        {acoes.length > 0 && <Stack direction="row" sx={{ flexShrink: 0 }}>{acoes}</Stack>}
      </Stack>
    </Box>
  );
}

// A sombra interna faz o hover valer também nas células opacas (sticky) e
// sobre a faixa de domingo/feriado, que um bgcolor no lugar delas quebraria.
const hoverLinha = {
  "&:hover td": {
    boxShadow: (theme: Theme) => `inset 0 0 0 999px ${alpha(theme.palette.text.primary, 0.04)}`,
  },
};

// "02/09/2026 — Trabalha — Comercial — 08:00 às 18:00 — alterado à mão — <infração>". Os gestos de
// edição ficam na legenda, não na dica.
function dicaDaCelula(iso: string, celula: CelulaGrade, infracao?: string) {
  const partes = [formatDataBR(iso), rotuloDoTipo(celula.tipo), celula.horario, celula.alterado && "alterado à mão", infracao];
  return partes.filter(Boolean).join(" — ");
}

function EspelhoLinhaBase({
  colaborador,
  colunas,
  celulas,
  colunasHistorico = [],
  celulasHistorico,
  totais,
  domingosTrabalhados,
  mostrarHoras = false,
  mostrarTotais = true,
  estilos,
  fundoHora,
  onCiclar,
  onTrocarCarga,
  onVerLog,
  onImprimirEscalinha,
  infracoes,
  pendencia,
}: EspelhoLinhaProps) {
  // Da direita para a esquerda: TH, TF, TT e DT.
  const aposHoras = mostrarHoras ? LARGURA_TOTAL : 0;
  const larguraTotais = mostrarTotais ? aposHoras + LARGURA_TOTAL * 2 : 0;
  return (
    <Box component="tr" sx={hoverLinha}>
      <CelulaNome
        titulo={colaborador.nome}
        subtitulo={subtitulo(colaborador)}
        pendencia={pendencia}
        acoes={[
          onVerLog && (
            <Tooltip key="log" title="Histórico de alterações do colaborador">
              <IconButton size="small" aria-label={`Histórico de ${colaborador.nome}`} onClick={() => onVerLog(colaborador)}>
                <History fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
          onImprimirEscalinha && (
            <Tooltip key="escalinha" title="Imprimir a escalinha do colaborador">
              <IconButton
                size="small"
                aria-label={`Escalinha de ${colaborador.nome}`}
                onClick={() => onImprimirEscalinha(colaborador)}
              >
                <Print fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        ].filter(Boolean)}
      />
      {colunasHistorico.map((c, i) => {
        const celula = celulasHistorico?.get(c.iso);
        const tipo = celula?.tipo ?? "";
        const estilo = estilos[tipo] ?? estilos[""]!;
        return (
          <Box
            component="td"
            key={`h-${c.iso}`}
            title={
              celula && tipo
                ? [`${formatDataBR(c.iso)} (mês anterior)`, rotuloDoTipo(tipo), celula.horario].filter(Boolean).join(" — ")
                : undefined
            }
            sx={[celulaBase, faixa(c), historicoAtenuado, i === colunasHistorico.length - 1 && separadorHistorico]}
          >
            {celula && tipo && (
              <SeloTipo tipo={celula.entrada ?? celula.rotulo} estilo={estilo} cor={corDaJornada(celula.cor, fundoHora)} />
            )}
          </Box>
        );
      })}
      {colunas.map((c) => {
        const celula = celulas?.get(c.dia);
        const tipo = celula?.tipo ?? "";
        const estilo = estilos[tipo] ?? estilos[""]!;
        const infracao = infracoes?.get(c.iso);
        const editavel = !!onCiclar && proximoTipo(tipo) !== null;
        const trocaCarga = !!onTrocarCarga && tipo === "T";
        return (
          <Box
            component="td"
            key={c.dia}
            title={celula && tipo ? dicaDaCelula(c.iso, celula, infracao) : undefined}
            onDoubleClick={editavel ? () => onCiclar(colaborador.id, c.iso, tipo) : undefined}
            onContextMenu={
              trocaCarga
                ? (e: MouseEvent) => {
                    e.preventDefault();
                    onTrocarCarga(colaborador.id, c.iso);
                  }
                : undefined
            }
            sx={[celulaBase, faixa(c), editavel && { cursor: "pointer", userSelect: "none" }]}
          >
            {celula && tipo && (
              <SeloTipo
                tipo={celula.entrada ?? celula.rotulo}
                estilo={estilo}
                destaque={!!infracao}
                cor={corDaJornada(celula.cor, fundoHora)}
                semHorario={celula.semHorario}
                alterado={celula.alterado}
              />
            )}
          </Box>
        );
      })}
      {domingosTrabalhados !== undefined && (
        <Box
          component="td"
          sx={{
            ...celulaBase,
            ...fixaDireita(larguraTotais),
            fontWeight: 700,
            fontSize: 13,
            borderLeft: "1px solid",
            borderLeftColor: "divider",
          }}
        >
          {domingosTrabalhados}
        </Box>
      )}
      {mostrarTotais && (
        <>
          <Box
            component="td"
            sx={{
              ...celulaBase,
              ...fixaDireita(aposHoras + LARGURA_TOTAL),
              fontWeight: 700,
              fontSize: 13,
              borderLeft: "1px solid",
              borderLeftColor: "divider",
            }}
          >
            {totais.trabalhados}
          </Box>
          <Box component="td" sx={{ ...celulaBase, ...fixaDireita(aposHoras), fontWeight: 700, fontSize: 13, color: "error.main" }}>
            {totais.folgas}
          </Box>
          {mostrarHoras && (
            <Box component="td" sx={{ ...celulaBase, ...fixaDireita(0), fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>
              {formatarHoras(totais.minutos)}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export const EspelhoLinha = memo(EspelhoLinhaBase);
